import { Box, Checkbox, Collapse, Divider, Group, Stack, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import { Permission } from 'model/PermissionDataProvider';
import { useMemo } from 'react';

const REVIEW_DESCRIPTIONS: Record<string, string> = {
  'approve-revisions':
    'Schvaľovanie a zamietnutie správ iných technikov. Vlastné správy vyžadujú samostatné oprávnenie.',
  'accept-revisions': 'Preberanie a vrátenie správ iných technikov. Vlastné správy vyžadujú samostatné oprávnenie.',
  'approve-forms':
    'Schvaľovanie a zamietnutie formulárov iných vypracovateľov. Vlastné formuláre vyžadujú samostatné oprávnenie.',
  'accept-forms':
    'Preberanie a vrátenie formulárov iných vypracovateľov. Vlastné formuláre vyžadujú samostatné oprávnenie.',
};

interface PermissionBoxProps {
  permissionGroup: string;
  permissions: Permission[];
  setPermission: (permissionId: number, checked: boolean) => void;
  hasPermission: (permissionId: number) => boolean;
  removePermissions: (permissions: number[]) => void;
  addPermissions: (permissions: number[]) => void;
  inheritedPermissions?: number[];
}

/**
 * Box displaying permission for selected users.
 */
export default function PermissionBox({
  permissionGroup,
  permissions,
  setPermission,
  hasPermission,
  removePermissions,
  addPermissions,
  inheritedPermissions = [],
}: PermissionBoxProps) {
  const theme = useMantineTheme();

  const [opened, { toggle }] = useDisclosure(true);

  const allChecked = useMemo(
    () => permissions.every((permission) => hasPermission(permission.permissionId)),
    [permissions, hasPermission]
  );

  const indeterminate = useMemo(
    () => permissions.some((permission) => hasPermission(permission.permissionId)) && !allChecked,
    [permissions, hasPermission, allChecked]
  );

  const permissionIds = useMemo(() => permissions.map((permission) => permission.permissionId), [permissions]);
  const inheritedPermissionIds = useMemo(() => new Set(inheritedPermissions), [inheritedPermissions]);
  const editablePermissionIds = useMemo(
    () => permissionIds.filter((permissionId) => !inheritedPermissionIds.has(permissionId)),
    [inheritedPermissionIds, permissionIds]
  );

  return (
    <Box>
      <Group
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            toggle();
          }
        }}
        position="apart"
        bg="gray.0"
        p={16}
        style={{ borderTopLeftRadius: '4px', borderTopRightRadius: '4px' }}
      >
        <Checkbox
          label={permissionGroup}
          // TODO change this when Mantine's version is higher than 7.0.0
          // Temporary hack - the indeterminate state is also "checked"
          // In newer versions, the checkbox has `data-indeterminate` attribute
          checked={allChecked || indeterminate}
          indeterminate={indeterminate}
          disabled={editablePermissionIds.length === 0}
          size="lg"
          onChange={() => {
            if (allChecked) {
              removePermissions(editablePermissionIds);
            } else {
              addPermissions(editablePermissionIds);
            }
          }}
        />
        <Box onClick={toggle}>
          {opened ? <IconChevronUp color={theme.colors.gray[7]} /> : <IconChevronDown color={theme.colors.gray[7]} />}
        </Box>
      </Group>
      {opened && (
        <Box px={16} bg="gray.0">
          <Divider color="gray.3" />
        </Box>
      )}
      <Collapse
        in={opened}
        bg="gray.0"
        style={{ borderBottomLeftRadius: '4px', borderBottomRightRadius: '4px' }}
        pt={16}
        pl={32}
        pb={24}
      >
        <Stack spacing={16}>
          {permissions.map((permission) => (
            <Checkbox
              key={permission.permissionId}
              label={permission.permissionName}
              description={REVIEW_DESCRIPTIONS[permission.slug]}
              checked={hasPermission(permission.permissionId)}
              disabled={inheritedPermissionIds.has(permission.permissionId)}
              size="md"
              onChange={(event) => setPermission(permission.permissionId, event.currentTarget.checked)}
            />
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}
